/// Client-side input validation for auth forms.
///
/// The server is still the source of truth — these checks mirror server
/// rules so we can surface problems immediately without a round-trip. We
/// deliberately keep them loose enough to match the server so we don't
/// accidentally reject inputs the server would accept.

export type ValidationResult = { ok: true } | { ok: false; message: string };

/// Match the RFC 5322 essentials: exactly one `@`, non-empty local and
/// domain parts, domain has at least one dot. We don't try to fully parse
/// email — the server does the authoritative check.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email: string): ValidationResult {
  const trimmed = email.trim();
  if (!trimmed) return { ok: false, message: 'Email is required.' };
  if (trimmed.length > 254) return { ok: false, message: 'Email is too long.' };
  if (!EMAIL_RE.test(trimmed))
    return { ok: false, message: "That doesn't look like a valid email address." };
  return { ok: true };
}

/// Mirror the server's password rule: at least 12 characters. Length is the
/// only requirement — composition rules tend to push users toward predictable
/// patterns without measurably improving security.
export function validatePassword(password: string, mode: 'login' | 'register'): ValidationResult {
  if (!password) return { ok: false, message: 'Password is required.' };
  if (mode === 'login') {
    // For login, only reject empty. The server will say the rest.
    return { ok: true };
  }
  if (password.length < 12) {
    return { ok: false, message: 'Password must be at least 12 characters.' };
  }
  return { ok: true };
}

/// Trim and cap profile-text fields to something sane. Names aren't
/// formally validated — the server stores whatever unicode you want —
/// but we do bound the length so a pasted 10 MB blob doesn't make it
/// across the wire.
export function validateProfileName(name: string): ValidationResult {
  const trimmed = name.trim();
  if (trimmed.length > 120) {
    return { ok: false, message: 'Name is too long (max 120 characters).' };
  }
  return { ok: true };
}
